import { useState } from 'react';
import Header from '../components/Layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useEffect } from 'react';
import useAuthStore from '../store/useAuthStore';

const Settings = () => {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [paymentSources, setPaymentSources] = useState([]);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'user' });
  const [newClient, setNewClient] = useState('');
  const [newCategory, setNewCategory] = useState({ name: '', type: 'expense' });
  const [newPaymentMode, setNewPaymentMode] = useState('');
  const [newPaymentSource, setNewPaymentSource] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [clientsRes, categoriesRes, modesRes, sourcesRes] = await Promise.all([
        api.get('/clients'),
        api.get('/categories'),
        api.get('/payment-modes'),
        api.get('/payment-sources'),
      ]);
      setClients(clientsRes.data);
      setCategories(categoriesRes.data);
      setPaymentModes(modesRes.data);
      setPaymentSources(sourcesRes.data);
      
      if (isAdmin) {
        const usersRes = await api.get('/users');
        setUsers(usersRes.data);
      }
    } catch (error) {
      console.error('Failed to load settings data:', error);
    }
  };

  const addUser = async () => {
    if (!newUser.name.trim() || !newUser.email.trim() || !newUser.password.trim()) {
      toast.error('Please fill all fields');
      return;
    }
    try {
      await api.post('/users', newUser);
      toast.success('User added successfully');
      setNewUser({ name: '', email: '', password: '', role: 'user' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add user');
    }
  };

  const deleteUser = async (email) => {
    if (email === 'branofy@gmail.com') {
      toast.error('Cannot delete super admin');
      return;
    }
    try {
      await api.delete(`/users/${email}`);
      toast.success('User deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const addClient = async () => {
    if (!newClient.trim()) return;
    try {
      await api.post('/clients', { name: newClient });
      toast.success('Client added');
      setNewClient('');
      loadData();
    } catch (error) {
      toast.error('Failed to add client');
    }
  };

  const deleteClient = async (id) => {
    try {
      await api.delete(`/clients/${id}`);
      toast.success('Client deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const addCategory = async () => {
    if (!newCategory.name.trim()) return;
    try {
      await api.post('/categories', newCategory);
      toast.success('Category added');
      setNewCategory({ name: '', type: 'expense' });
      loadData();
    } catch (error) {
      toast.error('Failed to add category');
    }
  };

  const deleteCategory = async (id) => {
    try {
      await api.delete(`/categories/${id}`);
      toast.success('Category deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const addPaymentMode = async () => {
    if (!newPaymentMode.trim()) return;
    try {
      await api.post('/payment-modes', { name: newPaymentMode });
      toast.success('Payment mode added');
      setNewPaymentMode('');
      loadData();
    } catch (error) {
      toast.error('Failed to add payment mode');
    }
  };

  const deletePaymentMode = async (id) => {
    try {
      await api.delete(`/payment-modes/${id}`);
      toast.success('Payment mode deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete payment mode');
    }
  };

  const addPaymentSource = async () => {
    if (!newPaymentSource.trim()) return;
    try {
      await api.post('/payment-sources', { name: newPaymentSource });
      toast.success('Payment source added');
      setNewPaymentSource('');
      loadData();
    } catch (error) {
      toast.error('Failed to add payment source');
    }
  };

  const deletePaymentSource = async (id) => {
    try {
      await api.delete(`/payment-sources/${id}`);
      toast.success('Payment source deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete payment source');
    }
  };

  return (
    <div className="min-h-screen">
      <Header title="Settings" subtitle="Manage master data and app configuration" />

      <div className="p-8">
        <Tabs defaultValue={isAdmin ? "users" : "clients"} className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1">
            {isAdmin && <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>}
            <TabsTrigger value="clients" data-testid="tab-clients">Clients</TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories">Categories</TabsTrigger>
            <TabsTrigger value="payment-modes" data-testid="tab-payment-modes">Payment Modes</TabsTrigger>
            <TabsTrigger value="payment-sources" data-testid="tab-payment-sources">Payment Sources</TabsTrigger>
          </TabsList>

          {isAdmin && (
            <TabsContent value="users">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-orange-600" />
                    Manage Users (Admin Only)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 mb-6 p-4 bg-slate-50 rounded-lg">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-slate-600 mb-1 block">Name</Label>
                        <Input
                          placeholder="Full name"
                          value={newUser.name}
                          onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                          className="bg-white"
                          data-testid="input-new-user-name"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600 mb-1 block">Email</Label>
                        <Input
                          placeholder="email@example.com"
                          type="email"
                          value={newUser.email}
                          onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                          className="bg-white"
                          data-testid="input-new-user-email"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-slate-600 mb-1 block">Password</Label>
                        <Input
                          placeholder="Password"
                          type="password"
                          value={newUser.password}
                          onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                          className="bg-white"
                          data-testid="input-new-user-password"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600 mb-1 block">Role</Label>
                        <Select value={newUser.role} onValueChange={(value) => setNewUser({ ...newUser, role: value })}>
                          <SelectTrigger className="bg-white" data-testid="select-user-role">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">View Only</SelectItem>
                            <SelectItem value="editor">Full Access</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Button onClick={addUser} className="w-full bg-orange-500 hover:bg-orange-600" data-testid="add-user-button">
                      <Plus className="w-4 h-4 mr-2" />
                      Add User
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {users.map((u) => (
                      <div key={u.email} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-slate-900 font-medium">{u.name}</span>
                            {u.email === 'branofy@gmail.com' && (
                              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-semibold">
                                SUPER ADMIN
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-sm text-slate-500">{u.email}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 
                              u.role === 'editor' ? 'bg-blue-100 text-blue-700' : 
                              'bg-slate-200 text-slate-700'
                            }`}>
                              {u.role === 'admin' ? 'Admin' : u.role === 'editor' ? 'Full Access' : 'View Only'}
                            </span>
                          </div>
                        </div>
                        {u.email !== 'branofy@gmail.com' && (
                          <button
                            onClick={() => deleteUser(u.email)}
                            className="text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50"
                            data-testid={`delete-user-${u.email}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          <TabsContent value="clients">
            <Card>
              <CardHeader>
                <CardTitle>Manage Clients</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-6">
                  <Input
                    placeholder="Client name"
                    value={newClient}
                    onChange={(e) => setNewClient(e.target.value)}
                    className="bg-slate-50"
                    data-testid="input-new-client"
                  />
                  <Button onClick={addClient} className="bg-orange-500 hover:bg-orange-600" data-testid="add-client-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add
                  </Button>
                </div>
                <div className="space-y-2">
                  {clients.map((client) => (
                    <div key={client.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="text-slate-900 font-medium">{client.name}</span>
                      <button
                        onClick={() => deleteClient(client.id)}
                        className="text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50"
                        data-testid={`delete-client-${client.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="categories">
            <Card>
              <CardHeader>
                <CardTitle>Manage Categories</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-6">
                  <Input
                    placeholder="Category name"
                    value={newCategory.name}
                    onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                    className="bg-slate-50 flex-1"
                    data-testid="input-new-category"
                  />
                  <select
                    value={newCategory.type}
                    onChange={(e) => setNewCategory({ ...newCategory, type: e.target.value })}
                    className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    data-testid="select-category-type"
                  >
                    <option value="expense">Expense</option>
                    <option value="project">Project</option>
                  </select>
                  <Button onClick={addCategory} className="bg-orange-500 hover:bg-orange-600" data-testid="add-category-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add
                  </Button>
                </div>
                <div className="space-y-2">
                  {categories.map((category) => (
                    <div key={category.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <span className="text-slate-900 font-medium">{category.name}</span>
                        <span className="ml-2 text-xs text-slate-500 bg-slate-200 px-2 py-0.5 rounded-full">
                          {category.type}
                        </span>
                      </div>
                      <button
                        onClick={() => deleteCategory(category.id)}
                        className="text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50"
                        data-testid={`delete-category-${category.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="payment-modes">
            <Card>
              <CardHeader>
                <CardTitle>Manage Payment Modes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-6">
                  <Input
                    placeholder="Payment mode name"
                    value={newPaymentMode}
                    onChange={(e) => setNewPaymentMode(e.target.value)}
                    className="bg-slate-50"
                    data-testid="input-new-payment-mode"
                  />
                  <Button onClick={addPaymentMode} className="bg-orange-500 hover:bg-orange-600" data-testid="add-payment-mode-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add
                  </Button>
                </div>
                <div className="space-y-2">
                  {paymentModes.map((mode) => (
                    <div key={mode.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="text-slate-900 font-medium">{mode.name}</span>
                      <button
                        onClick={() => deletePaymentMode(mode.id)}
                        className="text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50"
                        data-testid={`delete-payment-mode-${mode.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="payment-sources">
            <Card>
              <CardHeader>
                <CardTitle>Manage Payment Sources (Paid By)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-6">
                  <Input
                    placeholder="Payment source name"
                    value={newPaymentSource}
                    onChange={(e) => setNewPaymentSource(e.target.value)}
                    className="bg-slate-50"
                    data-testid="input-new-payment-source"
                  />
                  <Button onClick={addPaymentSource} className="bg-orange-500 hover:bg-orange-600" data-testid="add-payment-source-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add
                  </Button>
                </div>
                <div className="space-y-2">
                  {paymentSources.map((source) => (
                    <div key={source.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="text-slate-900 font-medium">{source.name}</span>
                      <button
                        onClick={() => deletePaymentSource(source.id)}
                        className="text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50"
                        data-testid={`delete-payment-source-${source.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Settings;